import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Header } from '../components/Header';
import { Portal } from '../components/Portal';
import { GozapMobileConnectModal } from '../components/GozapMobileConnectModal';
import { GozapProxyModal } from '../components/GozapProxyModal';
import { useSettings } from '../hooks/useSettings';
import { useTenant } from '../contexts/TenantContext';

function ProviderIcon({ provider, className = 'w-5 h-5' }: { provider: string; className?: string }) {
  if (provider === 'GOZAP') {
    return (
      <span className={`${className} inline-flex items-center justify-center rounded-full bg-teal-600 text-white font-bold`} style={{ fontSize: '0.55rem' }}>
        TM
      </span>
    );
  }

  return (
    <img
      src={
        provider === 'EVOLUTION' ? '/iconeevolutionapi.png' :
        provider === 'QUEPASA' ? '/iconequepasa.png' :
        '/iconewaha.png'
      }
      alt={provider}
      className={`${className} object-contain`}
    />
  );
}

function getProviderLabel(provider: string): string {
  switch (provider) {
    case 'EVOLUTION': return 'Evolution API';
    case 'QUEPASA': return 'Quepasa';
    case 'GOZAP': return 'Tavola Mobile';
    default: return 'Waha';
  }
}

// Componente para exibir contador do QR Code
function QRCountdown({ expiresAt }: { expiresAt: Date }) {
  const [timeLeft, setTimeLeft] = useState(0);

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const expiry = expiresAt.getTime();
      const difference = expiry - now;
      return Math.max(0, Math.ceil(difference / 1000));
    };

    setTimeLeft(calculateTimeLeft());

    const timer = setInterval(() => {
      const newTimeLeft = calculateTimeLeft();
      setTimeLeft(newTimeLeft);

      if (newTimeLeft <= 0) {
        clearInterval(timer);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [expiresAt]);

  if (timeLeft <= 0) {
    return <span className="text-red-600 text-sm">QR Code expirado</span>;
  }

  return (
    <span className="text-orange-600 text-sm">
      Expira em {timeLeft}s
    </span>
  );
}

interface WhatsAppSession {
  name: string; // Nome real usado na API (ex: vendas_c52982e8)
  displayName?: string; // Nome exibido ao usuário (ex: vendas)
  status: 'WORKING' | 'SCAN_QR_CODE' | 'STOPPED' | 'FAILED';
  provider: 'WAHA' | 'EVOLUTION' | 'QUEPASA' | 'GOZAP';
  qr?: string;
  qrExpiresAt?: Date;
  me?: {
    id: string;
    pushName: string;
  };
}


function isProviderConfigured(provider: string, settings: ReturnType<typeof useSettings>['settings']): boolean {
  switch (provider) {
    case 'WAHA':
      return !!(settings?.wahaHost && settings?.wahaApiKey);
    case 'EVOLUTION':
      return !!(settings?.evolutionHost && settings?.evolutionApiKey);
    case 'QUEPASA':
      return !!(settings?.quepasaUrl && settings?.quepasaLogin && settings?.quepasaPassword);
    case 'GOZAP':
      return !!(settings?.gozapHost && settings?.gozapAdminToken);
    default:
      return false;
  }
}

export default function WhatsAppConnectionsPage() {
  const { settings } = useSettings();
  // Bloqueio de provedor por tenant
  const providerLock = settings?.whatsappProviderLock ?? null;
  const availableProviders = ['WAHA', 'EVOLUTION', 'QUEPASA', 'GOZAP'].filter(p => isProviderConfigured(p, settings));
  const { selectedTenantId, loading: tenantLoading } = useTenant();
  const [sessions, setSessions] = useState<WhatsAppSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [newSessionName, setNewSessionName] = useState('');
  const [newSessionProvider, setNewSessionProvider] = useState<string>(providerLock ?? 'WAHA');
  // Garantir que o estado respeita um lock que carregue depois
  useEffect(() => {
    if (providerLock && newSessionProvider !== providerLock) {
      setNewSessionProvider(providerLock);
      return;
    }
    // Se o provedor selecionado não estiver configurado, seleciona o primeiro disponível
    if (!providerLock && availableProviders.length > 0 && !availableProviders.includes(newSessionProvider)) {
      setNewSessionProvider(availableProviders[0]);
    }
  }, [providerLock, settings, newSessionProvider]);
  const [isCreating, setIsCreating] = useState(false);
  const [loadingQR, setLoadingQR] = useState<string | null>(null);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [currentQRSession, setCurrentQRSession] = useState<WhatsAppSession | null>(null);
  const [createSessionModalOpen, setCreateSessionModalOpen] = useState(false);
  const [providerDropdownOpen, setProviderDropdownOpen] = useState(false);
  const [newEvolutionNumber, setNewEvolutionNumber] = useState('');
  const [newEvolutionGenerateQR, setNewEvolutionGenerateQR] = useState(true);
  const [newGozapConnectionMode, setNewGozapConnectionMode] = useState<'standard' | 'mobile'>('standard');
  const [newGozapNumber, setNewGozapNumber] = useState('');
  const [gozapMobileModalSession, setGozapMobileModalSession] = useState<WhatsAppSession | null>(null);
  const [gozapMobileModalPhone, setGozapMobileModalPhone] = useState('');
  const [gozapProxyModalSession, setGozapProxyModalSession] = useState<WhatsAppSession | null>(null);

  // Preload das imagens dos provedores para carregamento instantâneo
  useEffect(() => {
    const images = [
      '/iconewaha.png',
      '/iconeevolutionapi.png',
      '/iconequepasa.png'
    ];

    images.forEach(src => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  // Helper para fazer requisições autenticadas
  const authenticatedFetch = async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('auth_token');
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    // Adicionar tenant ID no header para SuperAdmin
    if (selectedTenantId) {
      (headers as Record<string, string>)['X-Tenant-Id'] = selectedTenantId;
    }

    return fetch(url, {
      ...options,
      headers,
    });
  };

  useEffect(() => {
    // Não carregar sessões até que o tenant esteja definido
    if (tenantLoading || !selectedTenantId) {
      return;
    }

    // Recarregar sessões quando o tenant mudar
    loadSessions();

    // Iniciar polling a cada 5 segundos
    const interval = setInterval(() => {
      loadSessions(false); // Não mostrar loading durante polling automático
    }, 5000);

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [selectedTenantId, tenantLoading]); // Recarrega quando o tenant mudar ou terminar de carregar

  // Polling para verificar se a conexão foi estabelecida quando o modal QR está aberto
  useEffect(() => {
    if (!qrModalOpen || !currentQRSession) {
      return;
    }

    const checkConnection = async () => {
      try {
        const response = await authenticatedFetch('/api/waha/sessions');
        if (response.ok) {
          const sessions = await response.json();
          const updatedSession = sessions.find((s: any) => s.name === currentQRSession.name);

          if (updatedSession) {
            console.log(`🔍 Polling - sessão ${currentQRSession.name}:`, {
              provider: updatedSession.provider,
              status: updatedSession.status,
              me: updatedSession.me
            });

            // Se a sessão mudou para WORKING (conectada)
            if (updatedSession.status === 'WORKING') {
              console.log(`✅ Sessão ${currentQRSession.name} conectada! Fechando modal...`);

              // Fechar modal
              setQrModalOpen(false);
              setCurrentQRSession(null);

              // Atualizar lista de sessões imediatamente
              await loadSessions(false);

              // Mostrar notificação de sucesso
              const userName = updatedSession.me?.pushName || 'Usuário';
              toast.success(`WhatsApp conectado com sucesso! Logado como: ${userName}`);
            }
          }
        }
      } catch (error) {
        console.error('Erro ao verificar status da conexão:', error);
      }
    };

    // Verificar imediatamente
    checkConnection();

    // Depois verificar a cada 2 segundos (polling mais frequente)
    const interval = setInterval(checkConnection, 2000);

    return () => {
      clearInterval(interval);
    };
  }, [qrModalOpen, currentQRSession]);

  const loadSessions = async (showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      const response = await authenticatedFetch('/api/waha/sessions');

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      // Processar dados das sessões incluindo QR code salvo no banco
      const processedSessions = data.map((session: any) => ({
        name: session.name,
        displayName: session.displayName || session.name,
        status: session.status || 'STOPPED',
        provider: session.provider || 'WAHA',
        me: session.me || null,
        qr: session.qr?.base64 ? session.qr.base64 : (typeof session.qr === 'string' ? session.qr : null),
        qrExpiresAt: session.qrExpiresAt ? new Date(session.qrExpiresAt) : undefined
      }));

      setSessions(processedSessions);
      if (showLoading) {
        setLoading(false);
      }
    } catch (error) {
      console.error('Erro ao carregar sessões:', error);
      if (showLoading) {
        toast.error('Erro ao carregar sessões WhatsApp');
        setLoading(false);
      }
    }
  };

  const createSession = async () => {
    if (!newSessionName.trim()) {
      toast.error('Digite um nome para a sessão');
      return;
    }

    // Validar se as configurações do provedor estão disponíveis
    if (newSessionProvider === 'WAHA' && (!settings?.wahaHost || !settings?.wahaApiKey)) {
      toast.error('Configure as credenciais WAHA nas configurações do sistema');
      return;
    }

    if (newSessionProvider === 'EVOLUTION' && (!settings?.evolutionHost || !settings?.evolutionApiKey)) {
      toast.error('Configure as credenciais Evolution API nas configurações do sistema');
      return;
    }

    if (newSessionProvider === 'QUEPASA' && (!settings?.quepasaUrl || !settings?.quepasaLogin || !settings?.quepasaPassword)) {
      toast.error('Configure as credenciais Quepasa nas configurações do sistema');
      return;
    }

    if (newSessionProvider === 'GOZAP' && (!settings?.gozapHost || !settings?.gozapAdminToken)) {
      toast.error('Configure as credenciais do Tavola Mobile nas configurações do sistema');
      return;
    }

    if (newSessionProvider === 'GOZAP' && newGozapConnectionMode === 'mobile' && !newGozapNumber.trim()) {
      toast.error('Informe o número do WhatsApp (com DDI) para o modo Mobile Virtual');
      return;
    }

    setIsCreating(true);
    try {
      const response = await authenticatedFetch('/api/waha/sessions', {
        method: 'POST',
        body: JSON.stringify({
          name: newSessionName.trim(),
          provider: newSessionProvider,
          ...(newSessionProvider === 'EVOLUTION' ? {
            number: newEvolutionNumber?.trim() || undefined,
            qrcode: newEvolutionGenerateQR
          } : {}),
          ...(newSessionProvider === 'GOZAP' ? {
            connectionMode: newGozapConnectionMode === 'mobile' ? 'mobile' : undefined,
            number: newGozapNumber?.trim() || undefined
          } : {})
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Erro desconhecido' }));

        // Verificar se é erro de quota
        if (errorData.upgradeRequired || (errorData.message && errorData.message.includes('Limite'))) {
          toast.error(errorData.message || 'Limite de conexões atingido. Faça upgrade do seu plano para continuar.', {
            duration: 6000,
            icon: '⚠️'
          });
          throw new Error(errorData.message);
        }

        throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`);
      }

      const createdSessionData = await response.json();

      toast.success(`Sessão ${newSessionProvider} criada com sucesso`);
      const createdSessionName = newSessionName.trim();
      const createdGozapMode = newGozapConnectionMode;
      const createdGozapPhone = newGozapNumber.trim();
      setNewSessionName('');
      setNewSessionProvider('WAHA');
      setNewEvolutionNumber('');
      setNewEvolutionGenerateQR(true);
      setNewGozapConnectionMode('standard');
      setNewGozapNumber('');

      // Recarregar imediatamente
      await loadSessions();

      // Se for Evolution e veio QR code, abrir modal automaticamente
      if (newSessionProvider === 'EVOLUTION' && createdSessionData.qrcode?.base64) {
        setTimeout(async () => {
          // Buscar o nome real da sessão criada
          const sessions = await authenticatedFetch('/api/waha/sessions').then(r => r.json());
          const createdSession = sessions.find((s: any) => s.name.startsWith(createdSessionName));

          if (createdSession) {
            await openQRModal(createdSession.name);
          }
        }, 1000);
      }

      // Se for GoZap no modo mobile virtual, abrir o wizard de conexão por telefone
      if (newSessionProvider === 'GOZAP' && createdGozapMode === 'mobile') {
        setTimeout(async () => {
          const sessions = await authenticatedFetch('/api/waha/sessions').then(r => r.json());
          const createdSession = sessions.find((s: any) => s.name.startsWith(createdSessionName));

          if (createdSession) {
            setGozapMobileModalPhone(createdGozapPhone);
            setGozapMobileModalSession({
              name: createdSession.name,
              displayName: createdSession.displayName || createdSession.name,
              status: createdSession.status || 'STOPPED',
              provider: 'GOZAP'
            });
          }
        }, 1000);
      }

      // Aguardar mais um pouco e recarregar novamente para pegar status atualizado
      setTimeout(() => {
        loadSessions(false);
      }, 2000);
    } catch (error) {
      console.error('Erro ao criar sessão:', error);
      // Não mostrar toast de erro genérico se já mostramos o toast específico de quota
      if (!(error instanceof Error && error.message.includes('Limite'))) {
        toast.error(error instanceof Error ? error.message : 'Erro ao criar sessão');
      }
    } finally {
      setIsCreating(false);
    }
  };

  const deleteSession = async (sessionName: string) => {
    if (!confirm(`Tem certeza que deseja remover a sessão "${sessionName}"?`)) {
      return;
    }

    try {
      const response = await authenticatedFetch(`/api/waha/sessions/${encodeURIComponent(sessionName)}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Erro desconhecido' }));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      toast.success('Sessão removida com sucesso');
      loadSessions();
    } catch (error) {
      console.error('Erro ao remover sessão:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao remover sessão');
    }
  };

  const restartSession = async (sessionName: string) => {
    try {
      const response = await authenticatedFetch(`/api/waha/sessions/${encodeURIComponent(sessionName)}/restart`, {
        method: 'POST'
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Erro desconhecido' }));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      toast.success('Sessão reiniciada');

      // Aguardar um pouco antes de recarregar
      setTimeout(() => {
        loadSessions(false);
      }, 1000);
    } catch (error) {
      console.error('Erro ao reiniciar sessão:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao reiniciar sessão');
    }
  };

  const requestQRCode = async (sessionName: string) => {
    setLoadingQR(sessionName);
    try {
      // Primeiro, iniciar a sessão
      const startResponse = await authenticatedFetch(`/api/waha/sessions/${encodeURIComponent(sessionName)}/start`, {
        method: 'POST'
      });

      if (!startResponse.ok) {
        const errorData = await startResponse.json().catch(() => ({ error: 'Erro desconhecido' }));
        throw new Error(errorData.error || `HTTP ${startResponse.status}`);
      }

      toast.success('QR Code solicitado. Aguarde...');

      // Aguardar um pouco e abrir modal para mostrar QR
      setTimeout(async () => {
        await openQRModal(sessionName);
        loadSessions(false);
      }, 2000);

    } catch (error) {
      console.error('Erro ao solicitar QR Code:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao solicitar QR Code');
    } finally {
      setLoadingQR(null);
    }
  };

  const openQRModal = async (sessionName: string) => {
    try {
      // Primeiro, verificar se já temos um QR salvo no banco para esta sessão
      const currentSession = sessions.find(s => s.name === sessionName);

      if (currentSession?.qr && currentSession?.qrExpiresAt && currentSession.qrExpiresAt > new Date()) {
        // Usar QR code já salvo no banco se ainda não expirou
        const sessionWithQR: WhatsAppSession = {
          name: sessionName,
          status: 'SCAN_QR_CODE',
          provider: currentSession.provider,
          qr: currentSession.qr,
          qrExpiresAt: currentSession.qrExpiresAt,
          me: currentSession.me
        };

        setCurrentQRSession(sessionWithQR);
        setQrModalOpen(true);
        return;
      }

      // Primeiro, iniciar a sessão para gerar o QR
      const startResponse = await authenticatedFetch(`/api/waha/sessions/${encodeURIComponent(sessionName)}/start`, {
        method: 'POST'
      });

      if (!startResponse.ok) {
        const startError = await startResponse.json();
        toast.error(`Erro ao iniciar sessão: ${startError.error || 'Erro desconhecido'}`);
        return;
      }

      // Aguardar um pouco para a sessão inicializar
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Agora buscar QR através do backend (que vai rotear para API correta)
      const qrResponse = await authenticatedFetch(`/api/waha/sessions/${encodeURIComponent(sessionName)}/auth/qr`);

      if (qrResponse.ok) {
        const qrData = await qrResponse.json();

        const qrValue = qrData.qr?.base64 ? qrData.qr.base64 : (typeof qrData.qr === 'string' ? qrData.qr : null);
        if (qrValue) {
          const sessionWithQR: WhatsAppSession = {
            name: sessionName,
            status: 'SCAN_QR_CODE',
            provider: currentSession?.provider || 'WAHA',
            qr: qrValue,
            qrExpiresAt: qrData.expiresAt ? new Date(qrData.expiresAt) : new Date(Date.now() + 60000),
            me: undefined
          };

          setCurrentQRSession(sessionWithQR);
          setQrModalOpen(true);

          // Recarregar sessões para obter dados atualizados do banco
          setTimeout(() => {
            loadSessions(false);
          }, 1000);
        } else {
          toast.error('QR Code não disponível');
        }
      } else {
        const errorData = await qrResponse.json();
        toast.error(errorData.error || 'Erro ao buscar QR Code');
      }
    } catch (error) {
      console.error('Erro ao buscar QR Code:', error);
      toast.error('Erro ao buscar QR Code');
    }
  };

  const closeQRModal = () => {
    setQrModalOpen(false);
    setCurrentQRSession(null);
    // Recarregar sessões para verificar se conectou
    loadSessions(false);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'WORKING':
        return 'bg-green-100 text-green-800';
      case 'SCAN_QR_CODE':
        return 'bg-yellow-100 text-yellow-800';
      case 'STOPPED':
        return 'bg-gray-100 text-gray-800';
      case 'FAILED':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'WORKING':
        return 'Conectado';
      case 'SCAN_QR_CODE':
        return 'Aguardando QR Code';
      case 'STOPPED':
        return 'Parado';
      case 'FAILED':
        return 'Erro';
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
        <span className="ml-2 text-gray-600">Carregando conexões...</span>
      </div>
    );
  }

  return (
    <>
      <Header
        title="Conexões WhatsApp"
        subtitle={`${sessions.length} ${sessions.length === 1 ? 'sessão ativa' : 'sessões ativas'}`}
        actions={
          <div className="flex gap-3">
            <button
              onClick={() => setCreateSessionModalOpen(true)}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm font-medium transition-colors"
            >
              + Nova Sessão
            </button>
            <button
              onClick={() => loadSessions()}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm font-medium transition-colors"
            >
              Atualizar
            </button>
          </div>
        }
      />

      <div className="p-6 space-y-6">

      {/* Lista de Sessões */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Sessões Ativas</h3>
        </div>

        {sessions.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            Nenhuma sessão WhatsApp encontrada. Crie uma nova sessão para começar.
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {sessions.map((session) => (
              <div key={session.name} className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h4 className="text-lg font-medium text-gray-900">{session.displayName || session.name}</h4>
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        session.provider === 'EVOLUTION' ? 'bg-green-100 text-green-800' :
                        session.provider === 'QUEPASA' ? 'bg-purple-100 text-purple-800' :
                        session.provider === 'GOZAP' ? 'bg-teal-100 text-teal-800' :
                        'bg-green-100 text-green-800'
                      }`}>
                        <ProviderIcon provider={session.provider} className="w-4 h-4" />
                        {getProviderLabel(session.provider)}
                      </span>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(session.status)}`}>
                        {getStatusText(session.status)}
                      </span>
                    </div>

                    {session.me && (
                      <p className="text-sm text-gray-600">
                        Conectado como: <span className="font-medium">{session.me.pushName}</span> ({session.me.id})
                      </p>
                    )}
                  </div>

                  <div className="flex gap-2 ml-4">
                    {session.status === 'STOPPED' && session.provider === 'GOZAP' && (
                      <>
                        <button
                          onClick={() => requestQRCode(session.name)}
                          disabled={loadingQR === session.name}
                          className="px-3 py-1 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 disabled:opacity-50"
                        >
                          {loadingQR === session.name ? 'Solicitando...' : 'QR/Código'}
                        </button>
                        <button
                          onClick={() => {
                            setGozapMobileModalPhone('');
                            setGozapMobileModalSession(session);
                          }}
                          className="px-3 py-1 bg-teal-600 text-white text-sm rounded-md hover:bg-teal-700"
                        >
                          Mobile Virtual
                        </button>
                      </>
                    )}
                    {session.status === 'STOPPED' && session.provider !== 'GOZAP' && (
                      <button
                        onClick={() => requestQRCode(session.name)}
                        disabled={loadingQR === session.name}
                        className="px-3 py-1 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 disabled:opacity-50"
                      >
                        {loadingQR === session.name ? 'Solicitando...' : 'Conectar'}
                      </button>
                    )}
                    {session.status === 'SCAN_QR_CODE' && (
                      <button
                        onClick={() => openQRModal(session.name)}
                        className="px-3 py-1 bg-green-600 text-white text-sm rounded-md hover:bg-green-700"
                      >
                        Ver QR Code
                      </button>
                    )}
                    {session.provider === 'GOZAP' && (
                      <button
                        onClick={() => setGozapProxyModalSession(session)}
                        className="px-3 py-1 bg-gray-600 text-white text-sm rounded-md hover:bg-gray-700"
                      >
                        Proxy
                      </button>
                    )}
                    <button
                      onClick={() => restartSession(session.name)}
                      className="px-3 py-1 bg-yellow-600 text-white text-sm rounded-md hover:bg-yellow-700"
                    >
                      Reiniciar
                    </button>
                    <button
                      onClick={() => deleteSession(session.name)}
                      className="px-3 py-1 bg-red-600 text-white text-sm rounded-md hover:bg-red-700"
                    >
                      Remover
                    </button>
                  </div>
                </div>

              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal Criar Sessão */}
      {createSessionModalOpen && (
        <Portal>
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center backdrop-blur-sm" style={{ zIndex: 9999 }}>
            <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md border border-gray-100 m-4 max-h-[90vh] overflow-y-auto" role="dialog" aria-labelledby="create-session-title">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl mx-auto mb-4 flex items-center justify-center">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <h2 id="create-session-title" className="text-2xl font-bold text-gray-900">
                Nova Sessão WhatsApp
              </h2>
              <p className="text-gray-500 mt-2">
                Digite um nome para identificar esta sessão
              </p>
            </div>

            <div className="space-y-6">
              <div>
                <label htmlFor="session-provider" className="block text-sm font-semibold text-gray-700 mb-2">
                  Provedor WhatsApp *
                </label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => !isCreating && !providerLock && setProviderDropdownOpen(!providerDropdownOpen)}
                    className="w-full px-4 py-3 pl-12 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm bg-white text-left flex items-center justify-between"
                    disabled={isCreating || !!providerLock}
                  >
                    <span className="flex items-center gap-3">
                      <ProviderIcon provider={newSessionProvider} className="w-5 h-5" />
                      {getProviderLabel(newSessionProvider)}
                    </span>
                    <svg className={`w-4 h-4 text-gray-400 transition-transform ${providerDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {providerDropdownOpen && (
                    <>
                      <div
                        className="fixed inset-0 z-10"
                        onClick={() => setProviderDropdownOpen(false)}
                      />
                      <div className="absolute z-20 w-full mt-2 bg-white border border-gray-300 rounded-xl shadow-lg overflow-hidden">
                        {availableProviders.length === 0 && (
                          <div className="px-4 py-3 text-sm text-gray-500">
                            Nenhum provedor configurado. Peça ao Super Admin para configurar em Integrações.
                          </div>
                        )}
                        {availableProviders.includes('WAHA') && (
                        <button
                          type="button"
                          onClick={() => {
                            setNewSessionProvider('WAHA');
                            setProviderDropdownOpen(false);
                          }}
                          className={`w-full px-4 py-3 text-left flex items-center gap-3 hover:bg-green-50 transition-colors ${
                            newSessionProvider === 'WAHA' ? 'bg-green-50' : ''
                          }`}
                        >
                          <img src="/iconewaha.png" alt="Waha" className="w-5 h-5 object-contain" />
                          <span className="text-sm">Waha</span>
                        </button>
                        )}
                        {availableProviders.includes('EVOLUTION') && (
                        <button
                          type="button"
                          onClick={() => {
                            setNewSessionProvider('EVOLUTION');
                            setProviderDropdownOpen(false);
                          }}
                          className={`w-full px-4 py-3 text-left flex items-center gap-3 hover:bg-green-50 transition-colors ${
                            newSessionProvider === 'EVOLUTION' ? 'bg-green-50' : ''
                          }`}
                        >
                          <img src="/iconeevolutionapi.png" alt="Evolution API" className="w-5 h-5 object-contain" />
                          <span className="text-sm">Evolution API</span>
                        </button>
                        )}
                        {availableProviders.includes('QUEPASA') && (
                        <button
                          type="button"
                          onClick={() => {
                            setNewSessionProvider('QUEPASA');
                            setProviderDropdownOpen(false);
                          }}
                          className={`w-full px-4 py-3 text-left flex items-center gap-3 hover:bg-green-50 transition-colors ${
                            newSessionProvider === 'QUEPASA' ? 'bg-green-50' : ''
                          }`}
                        >
                          <img src="/iconequepasa.png" alt="Quepasa" className="w-5 h-5 object-contain" />
                          <span className="text-sm">Quepasa</span>
                        </button>
                        )}
                        {availableProviders.includes('GOZAP') && (
                        <button
                          type="button"
                          onClick={() => {
                            setNewSessionProvider('GOZAP');
                            setProviderDropdownOpen(false);
                          }}
                          className={`w-full px-4 py-3 text-left flex items-center gap-3 hover:bg-green-50 transition-colors ${
                            newSessionProvider === 'GOZAP' ? 'bg-green-50' : ''
                          }`}
                        >
                          <ProviderIcon provider="GOZAP" className="w-5 h-5" />
                          <span className="text-sm">Tavola Mobile</span>
                        </button>
                        )}
                      </div>
                    </>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Escolha o provedor para conectar ao WhatsApp
                </p>
              </div>

              <div>
                <label htmlFor="session-name" className="block text-sm font-semibold text-gray-700 mb-2">
                  Nome da Sessão *
                </label>
                <input
                  id="session-name"
                  type="text"
                  value={newSessionName}
                  onChange={(e) => setNewSessionName(e.target.value)}
                  placeholder="Ex: vendas, suporte, atendimento"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                  disabled={isCreating}
                  autoFocus
                />
                <p className="text-xs text-gray-500 mt-2">
                  Use um nome descritivo para facilitar a identificação
                </p>
              </div>

              {newSessionProvider === 'EVOLUTION' && (
                <div className="space-y-4">
                  <div>
                    <label htmlFor="evolution-number" className="block text-sm font-semibold text-gray-700 mb-2">
                      Número do WhatsApp (com DDI)
                    </label>
                    <input
                      id="evolution-number"
                      type="text"
                      value={newEvolutionNumber}
                      onChange={(e) => setNewEvolutionNumber(e.target.value)}
                      placeholder="Ex: 559999999999"
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                      disabled={isCreating}
                    />
                    <p className="text-xs text-gray-500 mt-2">
                      Opcional. Informe para pareamento via número; sem número usa QR.
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      id="evolution-generate-qr"
                      type="checkbox"
                      checked={newEvolutionGenerateQR}
                      onChange={(e) => setNewEvolutionGenerateQR(e.target.checked)}
                      disabled={isCreating}
                    />
                    <label htmlFor="evolution-generate-qr" className="text-sm text-gray-700">
                      Gerar QR automaticamente ao criar a instância
                    </label>
                  </div>
                </div>
              )}

              {newSessionProvider === 'GOZAP' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Modo de Conexão
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => setNewGozapConnectionMode('standard')}
                        disabled={isCreating}
                        className={`px-4 py-3 rounded-xl border text-sm font-medium ${
                          newGozapConnectionMode === 'standard' ? 'border-green-500 bg-green-50 text-green-700' : 'border-gray-300 text-gray-600'
                        }`}
                      >
                        Padrão (QR/Código)
                      </button>
                      <button
                        type="button"
                        onClick={() => setNewGozapConnectionMode('mobile')}
                        disabled={isCreating}
                        className={`px-4 py-3 rounded-xl border text-sm font-medium ${
                          newGozapConnectionMode === 'mobile' ? 'border-teal-500 bg-teal-50 text-teal-700' : 'border-gray-300 text-gray-600'
                        }`}
                      >
                        Mobile Virtual (sem QR)
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      {newGozapConnectionMode === 'mobile'
                        ? 'Conecta por telefone + código de verificação, sem precisar de WhatsApp instalado em um celular.'
                        : 'Fluxo clássico de QR Code ou código de pareamento.'}
                    </p>
                  </div>

                  <div>
                    <label htmlFor="gozap-number" className="block text-sm font-semibold text-gray-700 mb-2">
                      Número do WhatsApp (com DDI){newGozapConnectionMode === 'mobile' ? ' *' : ''}
                    </label>
                    <input
                      id="gozap-number"
                      type="text"
                      value={newGozapNumber}
                      onChange={(e) => setNewGozapNumber(e.target.value)}
                      placeholder="Ex: 559999999999"
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                      disabled={isCreating}
                    />
                    <p className="text-xs text-gray-500 mt-2">
                      {newGozapConnectionMode === 'mobile'
                        ? 'Obrigatório para o modo Mobile Virtual.'
                        : 'Opcional. Informe para pareamento via número; sem número usa QR.'}
                    </p>
                  </div>
                </div>
              )}

              <div className="flex gap-4 pt-6">
                <button
                  type="button"
                  onClick={() => {
                    setCreateSessionModalOpen(false);
                    setNewSessionName('');
                    setNewSessionProvider('WAHA');
                    setNewEvolutionNumber('');
                    setNewEvolutionGenerateQR(true);
                    setNewGozapConnectionMode('standard');
                    setNewGozapNumber('');
                  }}
                  className="flex-1 bg-gray-100 text-gray-700 py-3 px-6 rounded-xl hover:bg-gray-200 font-medium transition-all duration-200 border border-gray-200"
                  disabled={isCreating}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    await createSession();
                    setCreateSessionModalOpen(false);
                  }}
                  disabled={isCreating || !newSessionName.trim() || availableProviders.length === 0}
                  className="flex-1 bg-green-600 text-white py-3 px-6 rounded-xl hover:bg-green-700 font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isCreating ? (
                    <>
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Criando...
                    </>
                  ) : (
                    'Criar Sessão'
                  )}
                </button>
              </div>
            </div>
          </div>
          </div>
        </Portal>
      )}

      {/* Modal do QR Code */}
      {qrModalOpen && currentQRSession && (
        <div className="qr-modal-overlay" onClick={closeQRModal}>
          <div className="qr-modal-content bg-white rounded-lg shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center p-6 border-b">
              <h3 className="text-lg font-medium text-gray-900">
                QR Code - {currentQRSession.displayName || currentQRSession.name} ({currentQRSession.provider})
              </h3>
              <button
                onClick={closeQRModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6">
              {currentQRSession?.qr ? (
                <div className="text-center">
                  {typeof currentQRSession.qr === 'string' && currentQRSession.qr.startsWith('data:image') ? (
                     <>
                       <h3 className="text-lg font-medium mb-4">Escaneie o QR Code com seu WhatsApp</h3>
                       <p className="text-sm text-gray-600 mb-6">
                         Abra o WhatsApp no seu celular, vá em <strong>Configurações → Aparelhos conectados → Conectar aparelho</strong> e escaneie o código abaixo
                       </p>
                       <div className="bg-white p-6 rounded-lg border inline-block shadow-lg">
                         <img
                           src={currentQRSession.qr}
                           alt="QR Code WhatsApp"
                           className="w-64 h-64 mx-auto block border-2 border-gray-200 rounded"
                         />
                       </div>
                     </>
                   ) : (
                     <>
                       <h3 className="text-lg font-medium mb-4">Código de pareamento</h3>
                       <p className="text-sm text-gray-600 mb-6">
                         Abra o WhatsApp no seu celular, vá em <strong>Configurações → Aparelhos conectados → Conectar com código de pareamento</strong> e digite o código abaixo
                       </p>
                       <div className="bg-gray-50 p-6 rounded-lg border inline-block shadow-lg">
                         <div className="text-3xl font-mono tracking-widest">
                           {currentQRSession.qr}
                         </div>
                       </div>
                     </>
                   )}
                  {currentQRSession.qrExpiresAt && (
                    <div className="mt-4">
                      <QRCountdown expiresAt={currentQRSession.qrExpiresAt} />
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3"></div>
                  <span className="text-gray-600">Gerando QR/Código...</span>
                  <p className="text-sm text-gray-500 mt-2">Aguarde um momento</p>
                </div>
              )}
            </div>

            <div className="flex flex-col gap-4 p-6 border-t">
              {(currentQRSession?.provider === 'EVOLUTION' || currentQRSession?.provider === 'WAHA') && (
                <div className="flex items-center gap-3">
                  <input
                    type="text"
                    placeholder="Número para pareamento (ex: 559999999999)"
                    className="flex-1 px-3 py-2 border rounded-md text-sm"
                    value={newEvolutionNumber}
                    onChange={(e) => setNewEvolutionNumber(e.target.value)}
                  />
                  <button
                    onClick={async () => {
                      try {
                        const resp = await authenticatedFetch(`/api/waha/sessions/${encodeURIComponent(currentQRSession.name)}/start?number=${encodeURIComponent(newEvolutionNumber)}`, { method: 'POST' });
                        const data = await resp.json();
                        const qrValue = data.qr?.base64 ? data.qr.base64 : (typeof data.qr === 'string' ? data.qr : null);
                        if (resp.ok && qrValue) {
                          setCurrentQRSession({
                            ...currentQRSession,
                            qr: qrValue,
                            qrExpiresAt: new Date(Date.now() + 300000)
                          });
                          toast.success('Código de pareamento gerado');
                        } else {
                          toast.error(data.error || 'Não foi possível gerar o código');
                        }
                      } catch (e) {
                        toast.error('Erro ao gerar código de pareamento');
                      }
                    }}
                    className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
                    disabled={!newEvolutionNumber.trim()}
                  >
                    Gerar código de pareamento
                  </button>
                </div>
              )}
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => openQRModal(currentQRSession.name)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Atualizar QR/Código
                </button>
                <button
                  onClick={closeQRModal}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {gozapMobileModalSession && (
        <GozapMobileConnectModal
          sessionName={gozapMobileModalSession.name}
          displayName={gozapMobileModalSession.displayName || gozapMobileModalSession.name}
          initialPhone={gozapMobileModalPhone}
          authenticatedFetch={authenticatedFetch}
          onClose={() => setGozapMobileModalSession(null)}
          onConnected={() => {
            setGozapMobileModalSession(null);
            loadSessions(false);
          }}
        />
      )}

      {gozapProxyModalSession && (
        <GozapProxyModal
          sessionName={gozapProxyModalSession.name}
          displayName={gozapProxyModalSession.displayName || gozapProxyModalSession.name}
          authenticatedFetch={authenticatedFetch}
          onClose={() => setGozapProxyModalSession(null)}
        />
      )}
      </div>
    </>
  );
}